import { isoDateTime, z } from "../zod.js";
import { Email } from "../identity/schemas.js";
import { Locale, Uuid } from "../identity/admin-schemas.js";

// Module 15 — Platform Console: the PR #7 v0 subset (spec 15 §5).
//
// Deferred to later PRs (deliberately absent — no stubs, D-026), per the
// spec 15 build-state ledger: health, billing/dunning, exports/deletion,
// announcements, staff PATCH/disable/TOTP-reset/resend-invite, audit-log read
// endpoints, PWA releases, and every /internal/platform/* surface. Plans,
// entitlements, overrides, tenant PATCH, the subdomain rename and usage are
// in entitlement-schemas.ts (M15-6, M15-7a).
//
// Every path below answers ONLY on the admin host (admin.assessly.ai) behind
// the platform staff cookie (`__Host-assessly_psid`) — except the tenant-side
// impersonation redemption endpoint, which answers on tenant subdomains
// (spec 16 §3.1.4a) and is registered with the identity tag.

/** Platform staff roles (spec 15 §2.11). */
export const PlatformStaffRole = z.enum([
  "platform_admin",
  "platform_support",
  "platform_billing",
  "platform_readonly",
]);

/**
 * Vertical pack keys accepted at tenant create (spec 15 §2.1: key VERBATIM,
 * validated against the packages/verticals registry — "new packs need no
 * migration"). The CONTRACT keeps only the shape (a kebab-case key); the
 * SERVICE validates membership against the registry and returns 422
 * PACK_UNKNOWN, so a new pack needs no contract change (spec ledger note).
 * packages/verticals is P1 — until it exists this constant IS the registry.
 */
export const VERTICAL_PACK_KEYS = ["cable-wire", "generic"] as const;
export const VerticalPack = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "kebab-case pack key");

export const PlatformRegion = z.enum([
  "ap-southeast-1",
  "eu-central-1",
  "me-central-1",
  "af-south-1",
]);

/** D-477: `fleet` acts on every tenant and staff member; `sandbox` only on
 * sandbox tenants and sandbox staff, and only where the deployment allows it. */
export const PlatformStaffScope = z.enum(["fleet", "sandbox"]).openapi("PlatformStaffScope");

export const PlatformStaffView = z
  .object({
    id: Uuid,
    email: z.string(),
    display_name: z.string(),
    role: PlatformStaffRole,
    status: z.enum(["invited", "totp_pending", "active", "disabled"]),
    totp_confirmed: z.boolean(),
    last_login_at: isoDateTime().nullable(),
    scope: PlatformStaffScope,
    created_at: isoDateTime(),
  })
  .openapi("PlatformStaffView");

// --- staff auth (spec 15 §3.1 / §5) ---

export const PlatformLoginRequest = z
  .object({
    email: Email,
    password: z.string().min(1).max(1024),
  })
  .openapi("PlatformLoginRequest");

export const PlatformLoginResponse = z
  .object({
    // 'totp' → present login_ticket to POST /v1/platform/auth/totp.
    // 'totp_enrollment_required' → staff is totp_pending; a restricted session
    // cookie is set that reaches ONLY the enrollment endpoints + /me + logout
    // (spec 15 §3.1.3; ledger note extends the §5 response enum).
    step: z.enum(["totp", "totp_enrollment_required"]),
    login_ticket: z.string().optional(),
  })
  .openapi("PlatformLoginResponse");

export const PlatformTotpRequest = z
  .object({
    login_ticket: z.string().min(16).max(256),
    code: z
      .string()
      .regex(/^\d{6}$/)
      .optional(),
    recovery_code: z.string().min(10).max(12).optional(),
  })
  .refine((v) => (v.code === undefined) !== (v.recovery_code === undefined), {
    message: "exactly one of code / recovery_code",
  })
  .openapi("PlatformTotpRequest");

export const PlatformTotpResponse = z
  .object({
    staff: PlatformStaffView,
    /** Unused recovery codes left after this sign-in (spec 15 §3.1 step 4,
     * D-570 ruling 1): at three or fewer the console asks for a new batch. */
    recovery_codes_remaining: z.number().int().min(0),
  })
  .openapi("PlatformTotpResponse");

export const PlatformMeResponse = z
  .object({
    id: Uuid,
    email: z.string(),
    /** The member's own name, for the console's header (M15-9). */
    display_name: z.string(),
    role: PlatformStaffRole,
    totp_confirmed: z.boolean(),
    scope: PlatformStaffScope,
    /** Unused recovery codes; null while the member is still enrolling. */
    recovery_codes_remaining: z.number().int().min(0).nullable(),
  })
  .openapi("PlatformMeResponse");

export const PlatformAcceptInviteRequest = z
  .object({
    token: z.string().min(16).max(256),
    // Staff policy (spec 15 §3.1.2): min 12 chars, zxcvbn score ≥ 3 —
    // enforced server-side; transport bounds only here.
    password: z.string().min(1).max(1024),
  })
  .openapi("PlatformAcceptInviteRequest");

export const PlatformAcceptInviteResponse = z
  .object({
    status: z.literal("totp_pending"),
  })
  .openapi("PlatformAcceptInviteResponse");

// --- staff recovery (spec 15 §3.1 steps 7 and 8, D-587) ---

export const PlatformPasswordResetRequest = z
  .object({
    email: Email,
  })
  .openapi("PlatformPasswordResetRequest");

export const PlatformPasswordResetCompleteRequest = z
  .object({
    token: z.string().min(16).max(256),
    // Staff policy (spec 15 §3.1.2): min 12 chars, zxcvbn score ≥ 3 —
    // enforced server-side; transport bounds only here.
    new_password: z.string().min(1).max(1024),
  })
  .openapi("PlatformPasswordResetCompleteRequest");

export const PlatformPasswordResetCompleteResponse = z
  .object({
    status: z.literal("password_set"),
  })
  .openapi("PlatformPasswordResetCompleteResponse");

export const PlatformBreakGlassRequest = z
  .object({
    // The one-time token whose sha256 the operator command wrote into the
    // grant parameter (D-587). Opaque here.
    token: z.string().min(16).max(256),
  })
  .openapi("PlatformBreakGlassRequest");

export const PlatformBreakGlassResponse = z
  .object({
    status: z.literal("totp_pending"),
    staff_id: Uuid,
    sessions_revoked: z.number().int().min(0),
  })
  .openapi("PlatformBreakGlassResponse");

export const PlatformTotpEnrollResponse = z
  .object({
    otpauth_uri: z.string(),
    secret: z.string(),
  })
  .openapi("PlatformTotpEnrollResponse");

export const PlatformTotpConfirmRequest = z
  .object({
    code: z.string().regex(/^\d{6}$/),
  })
  .openapi("PlatformTotpConfirmRequest");

export const PlatformRecoveryCodesResponse = z
  .object({
    recovery_codes: z
      .array(z.string().regex(/^[0-9A-HJKMNP-TV-Z]{5}-[0-9A-HJKMNP-TV-Z]{5}$/))
      .length(10),
  })
  .openapi("PlatformRecoveryCodesResponse");

export const PlatformRecoveryRegenerateRequest = z
  .object({
    // Fresh TOTP required (spec 15 §5).
    code: z.string().regex(/^\d{6}$/),
  })
  .openapi("PlatformRecoveryRegenerateRequest");

// --- staff management (v0 subset: list + invite) ---

export const PlatformStaffInviteRequest = z
  .object({
    email: Email,
    display_name: z.string().min(1).max(200),
    role: PlatformStaffRole,
    /** D-477 ruling 5: defaults to the inviter's own scope. A sandbox inviter
     * makes sandbox staff only, never a platform_admin (403
     * STAFF_SCOPE_DENIED); a sandbox invite needs a deployment that allows
     * sandbox staff (422 SANDBOX_STAFF_NOT_ALLOWED). */
    scope: PlatformStaffScope.optional(),
  })
  .openapi("PlatformStaffInviteRequest");

export const PlatformStaffListResponse = z
  .object({
    items: z.array(PlatformStaffView),
  })
  .openapi("PlatformStaffListResponse");

// --- staff management (M15-9, D-621; spec 15 §3.1 step 5, §4 S17, §5) ---

export const PlatformStaffPatchRequest = z
  .object({
    role: PlatformStaffRole.optional(),
    /** `disabled` disables; `active` re-enables a disabled member, who
     * returns as `invited` with a fresh invite and sets a new password and
     * second factor (D-621 ruling 5). */
    status: z.enum(["active", "disabled"]).optional(),
  })
  .strict()
  .refine((v) => v.role !== undefined || v.status !== undefined, {
    message: "name a role or a status",
  })
  .openapi("PlatformStaffPatchRequest");

export const PlatformStaffTotpResetRequest = z
  .object({
    reason: z.string().trim().min(5).max(500),
  })
  .strict()
  .openapi("PlatformStaffTotpResetRequest");

// --- support actions (M15-8, D-620; spec 15 §3.8, §4 S10, §5) ---

/** One person in a tenant's directory (§5: id, name, email, role, last login,
 * and nothing more). `roles` lists each role the person holds once. */
export const PlatformTenantUser = z
  .object({
    id: Uuid,
    display_name: z.string(),
    email: z.string(),
    roles: z.array(z.object({ code: z.string(), name: z.string() })),
    last_login_at: isoDateTime().nullable(),
    locked: z.boolean(),
  })
  .openapi("PlatformTenantUser");

export const PlatformTenantUserListResponse = z
  .object({ items: z.array(PlatformTenantUser) })
  .openapi("PlatformTenantUserListResponse");

export const PlatformSupportResetResponse = z
  .object({ status: z.literal("sent") })
  .openapi("PlatformSupportResetResponse");

export const PlatformRevokeSessionsResponse = z
  .object({ sessions_revoked: z.number().int().min(0) })
  .openapi("PlatformRevokeSessionsResponse");

export const PlatformRevokeAllSessionsRequest = z
  .object({
    /** The tenant's subdomain, typed out (S10's confirmation). */
    typed_confirmation: z.string().min(1).max(100),
  })
  .strict()
  .openapi("PlatformRevokeAllSessionsRequest");

export const PlatformRevokeAllSessionsResponse = z
  .object({
    sessions_revoked: z.number().int().min(0),
    grants_revoked: z.number().int().min(0),
  })
  .openapi("PlatformRevokeAllSessionsResponse");

// --- the platform audit log (M15-9, D-622, D-623; spec 15 §2.15, §4 S18) ---

export const PlatformAuditRow = z
  .object({
    id: z.number().int().min(1),
    occurred_at: isoDateTime(),
    staff_id: Uuid.nullable(),
    actor: z.string(),
    action: z.string(),
    tenant_id: Uuid.nullable(),
    entity_type: z.string().nullable(),
    entity_id: z.string().nullable(),
    // Optional, as the published document has always stated: zod 4 makes a
    // bare `z.unknown()` key required (D-643).
    before_state: z.unknown().optional(),
    after_state: z.unknown().optional(),
    metadata: z.record(z.string(), z.unknown()),
  })
  .openapi("PlatformAuditRow");

export const PlatformAuditLogResponse = z
  .object({
    items: z.array(PlatformAuditRow),
    next_cursor: z.string().nullable(),
  })
  .openapi("PlatformAuditLogResponse");

/** The newest verification of the platform chain (spec 15 §2.15a). */
export const PlatformChainVerification = z
  .object({
    verdict: z.enum(["intact", "broken", "never_verified"]),
    intact_through_id: z.number().int().min(0).nullable(),
    verified_at: isoDateTime().nullable(),
    anchored_at: isoDateTime().nullable(),
    break_at_id: z.number().int().nullable(),
    break_reason: z.enum(["id_gap", "prev_hash_mismatch", "curr_hash_mismatch"]).nullable(),
    /** Why the head was not anchored: `no_anchor_store`, `chain_broken`, or
     * `put_failed:<error>`. Null when it was. */
    anchor_error: z.string().nullable(),
    full_walk: z.boolean().nullable(),
    trigger: z.enum(["daily", "on_demand"]).nullable(),
  })
  .openapi("PlatformChainVerification");

// --- tenants (spec 15 §3.2 create path + v0 lifecycle actions) ---

// --- tenant health (spec 15 §2.21, §2.22, §3.10, §4 S9 and S15; M15-4,
// D-611 to D-613) ---

/**
 * The health dot (D-612): red when the tenant is down in the latest window or
 * holds an open alert, amber when a reading crosses a warning line, grey when
 * nothing recent was collected (no window in 15 minutes, or a tenant the
 * collector does not probe), green otherwise. Never green on missing data.
 */
export const PlatformHealthDot = z.enum(["green", "amber", "red", "grey"]);

/** Why the dot has its colour, worst first (D-612). */
export const PlatformHealthReason = z.enum([
  "down",
  "alert_open",
  "error_rate",
  "latency",
  "queue_lag",
  "parked_events",
  "stale",
  "not_probed",
  "health_drill",
]);

export const PlatformHealthAlertKind = z.enum([
  "canary_down",
  "queue_lag",
  "backup_stale",
  "fleet_collapse",
]);

export const PlatformTenantHealthSummary = z
  .object({
    dot: PlatformHealthDot,
    reasons: z.array(PlatformHealthReason),
    /** The window the dot was read from; null when nothing was collected. */
    captured_at: isoDateTime().nullable(),
  })
  .openapi("PlatformTenantHealthSummary");

/**
 * One collected window for one tenant (spec 15 §2.21). Every metric is null
 * when it was not collected that window, which is never the same as 0.
 * `request_count` 0 means the meter saw no traffic, so the error rate is 0 of
 * 0 and the p95 is null.
 */
export const PlatformHealthSnapshot = z
  .object({
    captured_at: isoDateTime(),
    subdomain_up: z.boolean().nullable(),
    probe_ms: z.number().int().min(0).nullable(),
    request_count: z.number().int().min(0).nullable(),
    http_error_rate_bp: z.number().int().min(0).max(10000).nullable(),
    p95_latency_ms: z.number().int().min(0).nullable(),
    queue_lag_seconds: z.number().int().min(0).nullable(),
    queue_depth: z.number().int().min(0).nullable(),
    health_drill: z.boolean(),
    /** Sources that failed this window (spec 15 §9.15), by name. */
    collect_errors: z.array(z.string()),
  })
  .openapi("PlatformHealthSnapshot");

/** One UTC day of a tenant's health (spec 15 §2.22). */
export const PlatformHealthDay = z
  .object({
    date: z.iso.date(),
    windows: z.number().int().min(0),
    probed_windows: z.number().int().min(0),
    uptime_bp: z.number().int().min(0).max(10000).nullable(),
    max_queue_lag_seconds: z.number().int().min(0).nullable(),
    avg_error_rate_bp: z.number().int().min(0).max(10000).nullable(),
    incidents: z.number().int().min(0),
  })
  .openapi("PlatformHealthDay");

export const PlatformTenantRef = z
  .object({
    id: Uuid,
    name: z.string(),
    subdomain: z.string(),
  })
  .openapi("PlatformTenantRef");

export const PlatformHealthAlert = z
  .object({
    id: Uuid,
    kind: PlatformHealthAlertKind,
    /** Null for a fleet incident (backup_stale, fleet_collapse). */
    tenant: PlatformTenantRef.nullable(),
    opened_at: isoDateTime(),
    last_seen_window: isoDateTime(),
    resolved_at: isoDateTime().nullable(),
    /** Down windows, lag seconds, backup age seconds or failing tenants. */
    value: z.number().int().nullable(),
    /** False for a health drill and for a tenant alert a fleet collapse held. */
    paged: z.boolean(),
  })
  .openapi("PlatformHealthAlert");

/** The fleet's backup reading (D-485 ruling 1): one fact for the pooled
 * database, never per tenant. `off` where the deployment has no backups to
 * watch (lanes, the showcase), and then no age is judged. */
export const PlatformFleetBackup = z
  .object({
    source: z.enum(["rds_pooled", "off"]),
    last_backup_at: isoDateTime().nullable(),
    latest_restorable_at: isoDateTime().nullable(),
    stale: z.boolean(),
  })
  .openapi("PlatformFleetBackup");

export const PlatformHealthWindow = z
  .object({
    start: isoDateTime(),
    seconds: z.number().int().min(10).max(300),
    collected_at: isoDateTime().nullable(),
    /** No window collected in the last 15 minutes (spec 15 §9.15). */
    stale: z.boolean(),
    tenants_probed: z.number().int().min(0),
    tenants_down: z.number().int().min(0),
    fleet_collapse: z.boolean(),
  })
  .openapi("PlatformHealthWindow");

export const PlatformHealthBoardRow = z
  .object({
    tenant: PlatformTenantRef.extend({ sandbox: z.boolean() }),
    health: PlatformTenantHealthSummary,
    /** The latest window's reading; null when this tenant has none in it. */
    snapshot: PlatformHealthSnapshot.nullable(),
    /** Probed windows up in the last 24 hours, basis points; null if none. */
    uptime_24h_bp: z.number().int().min(0).max(10000).nullable(),
    open_alerts: z.array(PlatformHealthAlertKind),
  })
  .openapi("PlatformHealthBoardRow");

/** S15, the fleet health board (spec 15 §4, §5 `GET /v1/platform/health`):
 * every active tenant, worst first. */
export const PlatformHealthBoardResponse = z
  .object({
    window: PlatformHealthWindow.nullable(),
    backup: PlatformFleetBackup.nullable(),
    fleet_alerts: z.array(PlatformHealthAlert),
    items: z.array(PlatformHealthBoardRow),
  })
  .openapi("PlatformHealthBoardResponse");

export const PlatformHealthSeriesWindow = z.enum(["24h", "7d", "30d"]);

/** S9, one tenant's health (spec 15 §5 `GET /v1/platform/tenants/:id/health`).
 * `windows` holds the last 24 hours of snapshots for `24h`; `days` holds the
 * daily rollups for `7d` and `30d`. */
export const PlatformTenantHealthResponse = z
  .object({
    tenant_id: Uuid,
    window: PlatformHealthSeriesWindow,
    health: PlatformTenantHealthSummary,
    health_drill: z.boolean(),
    current: PlatformHealthSnapshot.nullable(),
    backup: PlatformFleetBackup.nullable(),
    open_alerts: z.array(PlatformHealthAlert),
    recent_alerts: z.array(PlatformHealthAlert),
    windows: z.array(PlatformHealthSnapshot),
    days: z.array(PlatformHealthDay),
  })
  .openapi("PlatformTenantHealthResponse");

export const PlatformSetHealthDrillRequest = z
  .object({ health_drill: z.boolean() })
  .openapi("PlatformSetHealthDrillRequest");

/** One 5xx response the request meter saw (D-613). The route is the matched
 * template, never the raw path or query, so no address or search term is
 * stored; the request id finds the same request in the API's logs. */
export const PlatformRequestError = z
  .object({
    id: Uuid,
    occurred_at: isoDateTime(),
    host_kind: z.enum(["tenant", "admin", "other"]),
    tenant: PlatformTenantRef.nullable(),
    request_id: z.string(),
    method: z.string(),
    route: z.string(),
    status: z.number().int().min(500).max(599),
    duration_ms: z.number().int().min(0),
    problem_code: z.string().nullable(),
  })
  .openapi("PlatformRequestError");

export const PlatformLogSince = z.enum(["1h", "24h", "7d"]);

export const PlatformRequestErrorListResponse = z
  .object({
    items: z.array(PlatformRequestError),
    next_cursor: z.string().nullable(),
  })
  .openapi("PlatformRequestErrorListResponse");

export const PlatformHealthAlertListResponse = z
  .object({
    items: z.array(PlatformHealthAlert),
    next_cursor: z.string().nullable(),
  })
  .openapi("PlatformHealthAlertListResponse");

/** The collector's probe of one tenant (D-481 ruling 5), on the tenant host. */
export const TenantProbeResponse = z
  .object({
    status: z.literal("ok"),
    tenant: z.string(),
    time: isoDateTime(),
  })
  .openapi("TenantProbeResponse");

export const PlatformTenantSummary = z
  .object({
    id: Uuid,
    name: z.string(),
    subdomain: z.string(),
    status: z.string(),
    vertical_pack: z.string(),
    /** The plan's code (spec 15 §2.7; M15-6). A tenant with no assignment is
     * on `pilot` (D-615 ruling 1). */
    plan: z.string(),
    plan_id: Uuid,
    plan_name: z.string(),
    /** Distinct members active this UTC month (spec 15 §2.17), null until the
     * metering worker has written the month's rollup (M15-7a). */
    mau: z.number().int().min(0).nullable(),
    region: PlatformRegion,
    group_admin_email: z.string(),
    default_locale: Locale,
    currency: z.string(),
    default_timezone: z.string(),
    /** D-477 ruling 2: a sandbox tenant, which sandbox staff may act on. */
    sandbox: z.boolean(),
    /** D-612: the tenant's probe answers as down on purpose (sandbox only). */
    health_drill: z.boolean(),
    /** The S4/S5 health dot from the latest collected window (D-612). */
    health: PlatformTenantHealthSummary,
    created_at: isoDateTime(),
  })
  .openapi("PlatformTenantSummary");

export const ProvisioningStepStatus = z.enum(["pending", "done", "skipped"]);

export const PlatformProvisioningState = z
  .object({
    steps: z.array(
      z.object({
        name: z.string(),
        status: ProvisioningStepStatus,
      }),
    ),
    attempt_count: z.number().int().min(0),
    last_error: z.string().nullable(),
    processed_at: isoDateTime().nullable(),
  })
  .openapi("PlatformProvisioningState");

export const PlatformTenantDetail = z
  .object({
    tenant: PlatformTenantSummary,
    suspension: z
      .object({
        reason: z.string(),
        mode: z.enum(["grace", "immediate"]),
        suspended_at: isoDateTime(),
        grace_ends_at: isoDateTime().nullable(),
      })
      .nullable(),
    provisioning: PlatformProvisioningState.nullable(),
  })
  .openapi("PlatformTenantDetail");

let knownCurrencies: ReadonlySet<string> | null = null;

/**
 * Whether a code is on the runtime's ISO 4217 list (Intl, full ICU in Node
 * 22). A runtime without the list accepts any three capital letters, as the
 * pattern always has.
 */
export function isKnownCurrency(code: string): boolean {
  if (knownCurrencies === null) {
    const list = (
      Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
    ).supportedValuesOf?.("currency");
    knownCurrencies = new Set(list ?? []);
  }
  return knownCurrencies.size === 0 || knownCurrencies.has(code);
}

export const PlatformCreateTenantRequest = z
  .object({
    name: z.string().min(1).max(200),
    subdomain: z
      .string()
      .min(3)
      .max(63)
      .regex(/^[a-z0-9](?:[a-z0-9-]{1,61}[a-z0-9])$/),
    vertical_pack: VerticalPack,
    /** The plan to start on (spec 15 §3.2, M15-6). Absent: `pilot`. The pack
     * must be entitled on it (422 PACK_NOT_IN_PLAN). */
    plan_id: Uuid.optional(),
    region: PlatformRegion.optional(),
    default_locale: Locale.optional(),
    currency: z
      .string()
      .regex(/^[A-Z]{3}$/)
      // A tenant's currency is locked once money-bearing rows exist (spec 16
      // §2.1), so a typo such as "KTZ" would be permanent. The code must be
      // one the runtime's ISO 4217 list knows. A refinement, not a tighter
      // pattern: the OpenAPI document stays as it was, so no client breaks.
      .superRefine((code, ctx) => {
        if (!isKnownCurrency(code)) {
          ctx.addIssue({
            code: "custom",
            message: "not an ISO 4217 currency code",
          });
        }
      })
      .optional(),
    default_timezone: z.string().min(1).max(64).optional(),
    group_admin_email: Email,
  })
  .openapi("PlatformCreateTenantRequest");

export const PlatformTenantListResponse = z
  .object({
    items: z.array(PlatformTenantSummary),
    next_cursor: z.string().nullable(),
  })
  .openapi("PlatformTenantListResponse");

/**
 * The create wizard's live subdomain check (spec 15 §4 S11; the §8
 * `platform.check_subdomain` shape). Advice for the form, read without a
 * lock: the create decides, and still answers 409 for a name taken since.
 */
export const PlatformSubdomainCheckResponse = z
  .object({
    subdomain: z.string(),
    available: z.boolean(),
    reason: z.enum(["invalid", "reserved", "taken"]).nullable(),
  })
  .openapi("PlatformSubdomainCheckResponse");

/** One vertical pack the create wizard can offer (spec 15 §4 S11 pack cards),
 * named in every locale the pack carries. Read from the deployed
 * packages/verticals bundle, so a new pack needs no console change. */
export const PlatformVerticalPack = z
  .object({
    key: z.string(),
    version: z.string(),
    name: z.record(z.string(), z.string()),
    description: z.record(z.string(), z.string()),
  })
  .openapi("PlatformVerticalPack");

export const PlatformVerticalPackListResponse = z
  .object({ items: z.array(PlatformVerticalPack) })
  .openapi("PlatformVerticalPackListResponse");

export const SuspensionReason = z.enum([
  "billing_overdue",
  "compliance",
  "tenant_request",
  "security_incident",
]);

export const PlatformSuspendRequest = z
  .object({
    reason: SuspensionReason,
    mode: z.enum(["grace", "immediate"]),
    note: z.string().max(2000).optional(),
  })
  .openapi("PlatformSuspendRequest");

/** D-477 ruling 2: a fleet platform_admin marks a tenant sandbox, or clears it. */
export const PlatformSetSandboxRequest = z
  .object({ sandbox: z.boolean() })
  .openapi("PlatformSetSandboxRequest");

export const PlatformRestoreRequest = z
  .object({
    note: z.string().max(2000).optional(),
  })
  .openapi("PlatformRestoreRequest");

export const PlatformResendAdminInviteRequest = z
  .object({
    /** Defaults to tenant_meta.group_admin_email (spec 15 §5). */
    email: Email.optional(),
  })
  .openapi("PlatformResendAdminInviteRequest");

export const LifecycleEventItem = z
  .object({
    id: Uuid,
    tenant_id: Uuid,
    from_status: z.string(),
    to_status: z.string(),
    reason: z.string().nullable(),
    actor: z.string(),
    metadata: z.record(z.string(), z.unknown()),
    occurred_at: isoDateTime(),
  })
  .openapi("LifecycleEventItem");

export const LifecycleEventsResponse = z
  .object({
    items: z.array(LifecycleEventItem),
    next_cursor: z.string().nullable(),
  })
  .openapi("LifecycleEventsResponse");

// --- impersonation (spec 15 §2.14 / §3.7 steps 1–3 + 5) ---

export const ImpersonationMode = z.enum(["read_only", "act_as"]);

export const ImpersonationCreateRequest = z
  .object({
    tenant_id: Uuid,
    target_user_id: Uuid,
    mode: ImpersonationMode,
    reason: z.string().min(10).max(2000),
    ticket_ref: z.string().min(1).max(200),
    duration_minutes: z.number().int().min(1).max(480).default(60),
  })
  .openapi("ImpersonationCreateRequest");

export const ImpersonationGrantView = z
  .object({
    id: Uuid,
    tenant_id: Uuid,
    staff_id: Uuid,
    target_user_id: Uuid,
    mode: ImpersonationMode,
    reason: z.string(),
    ticket_ref: z.string(),
    status: z.enum(["pending_approval", "active", "ended", "expired", "revoked"]),
    approved_by_staff_id: Uuid.nullable(),
    starts_at: isoDateTime(),
    expires_at: isoDateTime(),
    ended_at: isoDateTime().nullable(),
    actions_count: z.number().int().min(0),
    created_at: isoDateTime(),
  })
  .openapi("ImpersonationGrantView");

export const ImpersonationCreateResponse = z
  .object({
    grant: ImpersonationGrantView,
    /** true ↔ 202: act_as by non-admin staff awaits platform_admin approval. */
    approval_required: z.boolean(),
  })
  .openapi("ImpersonationCreateResponse");

export const ImpersonationTokenResponse = z
  .object({
    /** https://<sub>.assessly.ai/impersonate?code=… — one-time, short-lived. */
    redirect_url: z.string(),
    code_expires_at: isoDateTime(),
  })
  .openapi("ImpersonationTokenResponse");

/** A grant as the list serves it (S10): who opened it and whom it targets.
 * `target_display_name` is null when module 16 holds no such user. */
// Spelled out flat rather than `.extend`ed: an extended registered schema is
// emitted as `allOf`, and the breaking-change check reads a list item that
// moved under `allOf` as every field of the grant removed (M15-8).
export const ImpersonationGrantListItem = z
  .object({
    ...ImpersonationGrantView.shape,
    staff_display_name: z.string(),
    target_display_name: z.string().nullable(),
  })
  .openapi("ImpersonationGrantListItem");

export const ImpersonationListResponse = z
  .object({
    items: z.array(ImpersonationGrantListItem),
    next_cursor: z.string().nullable(),
  })
  .openapi("ImpersonationListResponse");

/** The tenant banner's "End session" (spec 15 §4 S19). */
export const ImpersonationEndResponse = z
  .object({
    /** False when the grant was already over; its sessions are ended either way. */
    ended: z.boolean(),
  })
  .openapi("ImpersonationEndResponse");

/** Tenant-side redemption (spec 16 §3.1.4a) — answers on the tenant host. */
export const ImpersonationRedeemRequest = z
  .object({
    code: z.string().min(16).max(256),
  })
  .openapi("ImpersonationRedeemRequest");

export const ImpersonationRedeemResponse = z
  .object({
    tenant_id: Uuid,
    subdomain: z.string(),
    mode: ImpersonationMode,
    expires_at: isoDateTime(),
  })
  .openapi("ImpersonationRedeemResponse");

/**
 * Stable UPPER_SNAKE problem `code` values this module returns (spec 15 §5 +
 * the §0 build-state ledger; D-022). Codes shared with module 16 (SUBDOMAIN_*,
 * INVALID_TENANT_STATUS, ACCOUNT_LOCKED, INVALID_CREDENTIALS, PASSWORD_POLICY,
 * …) are listed in their owning module's array and reused with module 16's
 * shipped semantics — notably `SUBDOMAIN_RESERVED` is a 409 (module 16's
 * TenancyService), which §0 records supersedes §5's draft 422.
 */
export const platformProblemCodes = [
  "STAFF_INVITE_INVALID",
  "STAFF_INVITE_EXPIRED",
  "STAFF_EXISTS",
  "STAFF_NOT_TOTP_PENDING",
  "LOGIN_TICKET_INVALID",
  "STAFF_RESET_INVALID",
  "BREAK_GLASS_GRANT_INVALID",
  "BREAK_GLASS_REFUSED",
  "PACK_UNKNOWN",
  "GRANT_NOT_APPROVED",
  "GRANT_NOT_ACTIVE",
  "GRANT_ALREADY_LIVE",
  "IMPERSONATION_READ_ONLY",
  "IMPERSONATION_CODE_INVALID",
  "PROVISIONING_NOT_FAILED",
  "HEALTH_DRILL_NOT_SANDBOX",
  "HEALTH_DRILL",
  // M15-8 and M15-9 (D-620, D-621)
  "SUPPORT_RESET_NOT_ELIGIBLE",
  "CONFIRMATION_MISMATCH",
  "STAFF_SELF_CHANGE",
  "LAST_ADMIN",
  "INVITE_ALREADY_ACCEPTED",
  "STAFF_STATE_CONFLICT",
  "STAFF_PATCH_EMPTY",
  "INVALID_RANGE",
  "EXPORT_TAKES_NO_PAGE",
] as const;

export type PlatformProblemCode = (typeof platformProblemCodes)[number];
